import type { PacingTargetsConfig } from "@expert/app-config";
import type { CallCategory } from "@expert/shared-types";

/**
 * Which pacing UI the expert saw on a call — the Glow Bar or the current pill — recorded on every
 * finished call so the A/B analysis can compare the two. `null` means they saw neither.
 *
 * When querying: for a partner with no `pacingTargets` config the field is MISSING rather than `null`,
 * because `buildPacingFields` returns `{}` before it is set.
 */
export type PacingExperimentArm = "glow_bar" | "pill";

interface ExperimentArmInput {
    isPacingEnabled: boolean | undefined;
    pacingTargetsEnabled: boolean | undefined;
    isGlowBarPacingEnabled: boolean | undefined;
    isVoiceWorkspaceRedesignV2Enabled: boolean | undefined;
}

/**
 * Checks the same conditions the UI checks before rendering pacing (see `useIsGlowBarPacingActive`),
 * so the label matches what the expert saw. The partner's pacing config has to be on too: an expert
 * holding the flag with pacing off for their partner sees no pacing UI, so belongs in neither group.
 */
export function resolvePacingExperimentArm({
    isPacingEnabled,
    pacingTargetsEnabled,
    isGlowBarPacingEnabled,
    isVoiceWorkspaceRedesignV2Enabled,
}: ExperimentArmInput): PacingExperimentArm | null {
    if (!isPacingEnabled || !pacingTargetsEnabled) return null;
    return isGlowBarPacingEnabled && isVoiceWorkspaceRedesignV2Enabled ? "glow_bar" : "pill";
}

interface PacingInput {
    pacingConfig: PacingTargetsConfig | undefined;
    gaiaCallCategory: CallCategory | undefined;
    salesEligible: boolean;
    eligibleProductCount: number;
    hasPrimaryProduct: boolean;
    actualSeconds: number | undefined;
    /** Resolve with {@link resolvePacingExperimentArm}. */
    experimentArm: PacingExperimentArm | null;
}

export function buildPacingFields({
    pacingConfig,
    gaiaCallCategory,
    salesEligible,
    eligibleProductCount,
    hasPrimaryProduct,
    actualSeconds,
    experimentArm,
}: PacingInput): Record<string, unknown> {
    if (!pacingConfig) return {};

    const categoryTarget = gaiaCallCategory ? pacingConfig.callTypeTargets[gaiaCallCategory] : undefined;

    // When pacing is disabled, emit baseline timing fields for pilot vs non-pilot comparison
    if (!pacingConfig.enabled) {
        return {
            pacingEnabled: false,
            gaiaCallCategory: gaiaCallCategory ?? null,
            pacingActualSeconds: actualSeconds,
            salesEligible,
            eligibleProductCount,
            hasPrimaryProduct,
            pacingExperimentArm: experimentArm,
        };
    }

    // When pacing is enabled but Gaia hasn't classified the call (or classified as a category
    // without a specific configured target), fall back to the partner's defaultTargetSeconds
    // and flag the analytics event so consumers can distinguish default vs category-specific targets.
    const usingDefaultTarget = categoryTarget === undefined;
    const baseTarget = categoryTarget ?? pacingConfig.defaultTargetSeconds;
    const targetSeconds = baseTarget + (salesEligible ? pacingConfig.salesEligibleBufferSeconds : 0);

    const fields: Record<string, unknown> = {
        pacingEnabled: true,
        gaiaCallCategory: gaiaCallCategory ?? null,
        pacingBaseTargetSeconds: baseTarget,
        pacingTargetSeconds: targetSeconds,
        pacingUsingDefaultTarget: usingDefaultTarget,
        salesEligible,
        eligibleProductCount,
        hasPrimaryProduct,
        pacingExperimentArm: experimentArm,
    };

    if (actualSeconds !== undefined) {
        fields.pacingActualSeconds = actualSeconds;
        fields.pacingResult = actualSeconds <= targetSeconds ? "on_target" : "exceeded";
        if (actualSeconds > targetSeconds) {
            fields.pacingOverageSeconds = actualSeconds - targetSeconds;
        }
    }

    return fields;
}
